import React, { useEffect, useMemo, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ITINERARY_CATEGORIES } from '@/lib/categories';
import { ITINERARY } from '@/constants/testIds';
import { Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const NOTE_MAX = 300;
const DEFAULTS = {
    time_slot: '09:00',
    location_name: '',
    duration_minutes: 60,
    category: 'activity',
    description: '',
    note: '',
};

/**
 * Bottom-sheet on mobile / side-sheet on desktop for creating & editing an activity.
 * `mode` is 'add' or 'edit'. When 'edit', the initial activity + activityIndex are used.
 * onSubmit is (payload, mode) => Promise<void>; parent handles API + state.
 * onDelete is only called in edit mode when the user confirms deletion.
 */
export default function ActivityEditorSheet({
    open,
    onOpenChange,
    mode = 'add',
    dayNumber,
    activity = null,
    activityIndex = null,
    onSubmit,
    onDelete,
}) {
    const [values, setValues] = useState(DEFAULTS);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (mode === 'edit' && activity) {
            setValues({
                time_slot: activity.time_slot || '09:00',
                location_name: activity.location_name || '',
                duration_minutes: Number(activity.duration_minutes || 60),
                category: activity.category || 'activity',
                description: activity.description || '',
                note: activity.note || '',
            });
        } else {
            setValues(DEFAULTS);
        }
    }, [open, mode, activity]);

    const set = (k) => (v) => setValues((prev) => ({ ...prev, [k]: v }));
    const isValid = useMemo(() => {
        if (!values.location_name.trim()) return false;
        const d = Number(values.duration_minutes);
        if (!Number.isFinite(d) || d < 5 || d > 1440) return false;
        if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(values.time_slot)) return false;
        if ((values.note || '').length > NOTE_MAX) return false;
        return true;
    }, [values]);

    const handleSave = async () => {
        if (!isValid || saving) return;
        setSaving(true);
        try {
            await onSubmit({
                day_number: dayNumber,
                time_slot: values.time_slot,
                location_name: values.location_name.trim(),
                duration_minutes: Number(values.duration_minutes),
                category: values.category,
                description: values.description.trim(),
                note: values.note.trim(),
            }, mode);
            onOpenChange(false);
        } catch (err) {
            toast.error(err?.response?.data?.detail || err?.message || 'Could not save activity');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!onDelete || deleting) return;
        if (!window.confirm(`Delete "${activity?.location_name || 'this activity'}"?`)) return;
        setDeleting(true);
        try {
            await onDelete();
            onOpenChange(false);
        } catch (err) {
            toast.error(err?.response?.data?.detail || err?.message || 'Could not delete activity');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="bottom"
                data-testid={ITINERARY.activityEditorSheet}
                className="rounded-t-3xl sm:rounded-t-3xl border-t border-[var(--ns-border)] bg-[var(--ns-cream,#FDFAF3)] max-h-[92vh] overflow-y-auto sm:max-w-2xl sm:mx-auto sm:inset-x-0"
            >
                <SheetHeader className="text-left">
                    <SheetTitle className="font-display text-2xl font-semibold tracking-tight">
                        {mode === 'edit' ? 'Edit activity' : 'Add activity'}
                    </SheetTitle>
                    <SheetDescription>
                        Day {dayNumber}. Changes are visible to everyone in the trip.
                    </SheetDescription>
                </SheetHeader>

                <div className="mt-6 space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="sm:col-span-1">
                            <label className="text-[10px] uppercase tracking-[0.18em] text-[var(--ns-text3)] block mb-1.5">Start time</label>
                            <Input
                                type="time"
                                value={values.time_slot}
                                onChange={(e) => set('time_slot')(e.target.value)}
                                data-testid={ITINERARY.editorFieldTime}
                                className="rounded-xl border-[var(--ns-border)] bg-white h-11"
                            />
                        </div>
                        <div className="sm:col-span-1">
                            <label className="text-[10px] uppercase tracking-[0.18em] text-[var(--ns-text3)] block mb-1.5">Duration (min)</label>
                            <Input
                                type="number"
                                min={5}
                                max={1440}
                                step={5}
                                value={values.duration_minutes}
                                onChange={(e) => set('duration_minutes')(e.target.value)}
                                data-testid={ITINERARY.editorFieldDuration}
                                className="rounded-xl border-[var(--ns-border)] bg-white h-11 tabular-nums"
                            />
                        </div>
                        <div className="sm:col-span-1">
                            <label className="text-[10px] uppercase tracking-[0.18em] text-[var(--ns-text3)] block mb-1.5">Category</label>
                            <Select value={values.category} onValueChange={set('category')}>
                                <SelectTrigger data-testid={ITINERARY.editorFieldCategory} className="rounded-xl border-[var(--ns-border)] bg-white h-11">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="rounded-2xl">
                                    {ITINERARY_CATEGORIES.map((c) => (
                                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div>
                        <label className="text-[10px] uppercase tracking-[0.18em] text-[var(--ns-text3)] block mb-1.5">Location name</label>
                        <Input
                            value={values.location_name}
                            onChange={(e) => set('location_name')(e.target.value)}
                            placeholder="e.g. Marina Bay Sands SkyPark"
                            data-testid={ITINERARY.editorFieldLocation}
                            maxLength={120}
                            className="rounded-xl border-[var(--ns-border)] bg-white h-11"
                        />
                    </div>

                    <div>
                        <label className="text-[10px] uppercase tracking-[0.18em] text-[var(--ns-text3)] block mb-1.5">Description</label>
                        <Textarea
                            value={values.description}
                            onChange={(e) => set('description')(e.target.value)}
                            placeholder="What's the plan? Booking details, meeting point, anything the group should know."
                            rows={3}
                            maxLength={400}
                            data-testid={ITINERARY.editorFieldDescription}
                            className="rounded-2xl border-[var(--ns-border)] bg-white text-sm resize-none"
                        />
                    </div>

                    <div>
                        <label className="text-[10px] uppercase tracking-[0.18em] text-[var(--ns-text3)] flex items-center justify-between mb-1.5">
                            <span>Note <span className="normal-case tracking-normal text-[var(--ns-text3)]">(optional, visible to everyone)</span></span>
                            <span className="tabular-nums normal-case tracking-normal text-[var(--ns-text3)]">{(values.note || '').length}/{NOTE_MAX}</span>
                        </label>
                        <Textarea
                            value={values.note}
                            onChange={(e) => set('note')(e.target.value.slice(0, NOTE_MAX))}
                            placeholder="e.g. Book ahead — Nadia has the confirmation email."
                            rows={2}
                            maxLength={NOTE_MAX}
                            data-testid={ITINERARY.editorFieldNote}
                            className="rounded-2xl border-[var(--ns-border)] bg-white text-sm resize-none"
                        />
                    </div>
                </div>

                <div className="mt-7 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3">
                    {mode === 'edit' && onDelete && (
                        <Button
                            variant="ghost"
                            onClick={handleDelete}
                            disabled={deleting}
                            data-testid={ITINERARY.activityDeleteButton}
                            className="sm:mr-auto text-[var(--ns-clay)] hover:text-white hover:bg-[var(--ns-clay)] rounded-full h-11 px-4"
                        >
                            {deleting ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Trash2 size={14} className="mr-2" />}
                            Delete activity
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={saving || deleting}
                        data-testid={ITINERARY.editorCancelButton}
                        className="rounded-full h-11 px-5 border-[var(--ns-border)]"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={!isValid || saving}
                        data-testid={ITINERARY.editorSaveButton}
                        className="rounded-full h-11 px-6 text-white font-medium"
                        style={{ background: 'var(--ns-navy)' }}
                    >
                        {saving && <Loader2 size={14} className="mr-2 animate-spin" />}
                        {mode === 'edit' ? 'Save changes' : 'Add to Day ' + dayNumber}
                    </Button>
                </div>
            </SheetContent>
        </Sheet>
    );
}
